import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, createVNode as _createVNode, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = { class: "min-h-screen bg-gray-50 dark:bg-gray-900" }
const _hoisted_2 = { class: "flex items-center justify-between" }
const _hoisted_3 = { class: "flex items-center space-x-3" }
const _hoisted_4 = ["disabled"]
const _hoisted_5 = { class: "p-6 space-y-6" }
const _hoisted_6 = { class: "flex" }
const _hoisted_7 = { class: "ml-3" }
const _hoisted_8 = { class: "mt-2 text-sm text-red-700 dark:text-red-300" }
const _hoisted_9 = { class: "mt-4" }
const _hoisted_10 = { class: "flex items-center justify-center" }
const _hoisted_11 = { key: 2 }
const _hoisted_12 = {
  key: 3,
  class: "flex justify-center"
}

import { ref, reactive, onMounted, inject } from 'vue'
import { useTheme } from '../../utils/useTheme'
import AuditLogsFilters from './components/AuditLogsFilters.vue'
import AuditLogsTable from './components/AuditLogsTable.vue'
import { auditLogsService } from '../../services/audit/auditLogsService'
import type { AuditLog, AuditLogsFilters as AuditLogsFiltersType, AuditLogsQueryParams } from '../../types/audit-logs'
import { logger } from '../../utils/logger'

// Use theme via inject with a fallback

export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const theme = inject('theme', useTheme())

// State
const auditLogs = ref<AuditLog[]>([])
const loading = ref(false)
const error = ref<string | null>(null)
const resourceTypes = ref<string[]>([])

// Filters
const filters = reactive<AuditLogsFiltersType>({
  resourceType: '',
  resourceId: '',
  userId: '',
  startDate: '',
  endDate: '',
  httpMethod: []
})

// Pagination
const pagination = reactive({
  total: 0,
  hasMore: false,
  nextCursor: undefined as string | undefined
})

// Methods
const fetchAuditLogs = async (params: AuditLogsQueryParams = {}, append = false) => {
  try {
    loading.value = true
    error.value = null

    logger.log('Fetching audit logs with params:', params)

    const response = await auditLogsService.fetchAuditLogs(params)
    
    if (append) {
      auditLogs.value.push(...response.data)
    } else {
      auditLogs.value = response.data
    }

    pagination.total = response.pagination.total
    pagination.hasMore = response.pagination.hasMore
    pagination.nextCursor = response.pagination.nextCursor

    // Extract unique resource types for filter dropdown
    const types = new Set(auditLogs.value.map(log => log.resource.type))
    resourceTypes.value = Array.from(types).sort()

    logger.log('Audit logs fetched successfully:', {
      count: response.data.length,
      total: response.pagination.total,
      hasMore: response.pagination.hasMore
    })
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Failed to fetch audit logs'
    logger.error('Error fetching audit logs:', err)
  } finally {
    loading.value = false
  }
}

const refreshLogs = () => {
  const params = buildQueryParams()
  fetchAuditLogs(params, false)
}

const loadMore = () => {
  if (!pagination.nextCursor) return
  
  const params = buildQueryParams()
  params.cursor = pagination.nextCursor
  fetchAuditLogs(params, true)
}

const handleApplyFilters = () => {
  const params = buildQueryParams()
  fetchAuditLogs(params, false)
}

const buildQueryParams = (): AuditLogsQueryParams => {
  const params: AuditLogsQueryParams = {
    limit: 50
  }

  if (filters.resourceType) params.resourceType = filters.resourceType
  if (filters.resourceId) params.resourceId = filters.resourceId
  if (filters.userId) params.userId = filters.userId
  if (filters.startDate) params.startDate = new Date(filters.startDate).toISOString()
  if (filters.endDate) params.endDate = new Date(filters.endDate).toISOString()

  return params
}

// Lifecycle
onMounted(() => {
  fetchAuditLogs()
})

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: _normalizeClass([
        _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
        'border-b px-6 py-4'
      ])
    }, [
      _createElementVNode("div", _hoisted_2, [
        _createElementVNode("div", null, [
          _createElementVNode("h1", {
            class: _normalizeClass([
              _unref(theme).isDark.value ? 'text-gray-100' : 'text-gray-900',
              'text-2xl font-bold'
            ])
          }, " Audit Logs ", 2),
          _createElementVNode("p", {
            class: _normalizeClass([
              _unref(theme).isDark.value ? 'text-gray-400' : 'text-gray-600',
              'mt-1 text-sm'
            ])
          }, " View and filter platform audit logs ", 2)
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("button", {
            onClick: refreshLogs,
            disabled: loading.value,
            class: _normalizeClass([
              _unref(theme).isDark.value
                ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
                : 'bg-white hover:bg-gray-50 text-gray-700',
              'px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors',
              loading.value ? 'opacity-50 cursor-not-allowed' : ''
            ])
          }, [
            (_openBlock(), _createElementBlock("svg", {
              class: _normalizeClass([
                loading.value ? 'animate-spin' : '',
                'w-4 h-4 mr-2'
              ]),
              fill: "none",
              stroke: "currentColor",
              viewBox: "0 0 24 24"
            }, _cache[1] || (_cache[1] = [
              _createElementVNode("path", {
                "stroke-linecap": "round",
                "stroke-linejoin": "round",
                "stroke-width": "2",
                d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              }, null, -1)
            ]), 2)),
            _createTextVNode(" " + _toDisplayString(loading.value ? 'Loading...' : 'Refresh'), 1)
          ], 10, _hoisted_4)
        ])
      ])
    ], 2),
    _createElementVNode("div", _hoisted_5, [
      _createVNode(AuditLogsFilters, {
        filters: filters,
        "onUpdate:filters": _cache[0] || (_cache[0] = ($event: any) => ((filters) = $event)),
        "resource-types": resourceTypes.value,
        onApplyFilters: handleApplyFilters
      }, null, 8, ["filters", "resource-types"]),
      (error.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: _normalizeClass([
          _unref(theme).isDark.value ? 'bg-red-900 border-red-700' : 'bg-red-50 border-red-200',
          'border rounded-md p-4'
        ])
          }, [
            _createElementVNode("div", _hoisted_6, [
              _cache[3] || (_cache[3] = _createElementVNode("div", { class: "flex-shrink-0" }, [
                _createElementVNode("svg", {
                  class: "h-5 w-5 text-red-400",
                  fill: "none",
                  stroke: "currentColor",
                  viewBox: "0 0 24 24"
                }, [
                  _createElementVNode("path", {
                    "stroke-linecap": "round",
                    "stroke-linejoin": "round",
                    "stroke-width": "2",
                    d: "M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  })
                ])
              ], -1)),
              _createElementVNode("div", _hoisted_7, [
                _cache[2] || (_cache[2] = _createElementVNode("h3", { class: "text-sm font-medium text-red-800 dark:text-red-200" }, " Error loading audit logs ", -1)),
                _createElementVNode("div", _hoisted_8, _toDisplayString(error.value), 1),
                _createElementVNode("div", _hoisted_9, [
                  _createElementVNode("button", {
                    onClick: refreshLogs,
                    class: _normalizeClass([
                  _unref(theme).isDark.value
                    ? 'bg-red-800 hover:bg-red-700 text-red-100'
                    : 'bg-red-100 hover:bg-red-200 text-red-800',
                  'px-3 py-2 rounded-md text-sm font-medium transition-colors'
                ])
                  }, " Try Again ", 2)
                ])
              ])
            ])
          ], 2))
        : _createCommentVNode("", true),
      (loading.value && auditLogs.value.length === 0)
        ? (_openBlock(), _createElementBlock("div", {
            key: 1,
            class: _normalizeClass([
          _unref(theme).isDark.value ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200',
          'border rounded-lg p-8'
        ])
          }, [
            _createElementVNode("div", _hoisted_10, [
              _cache[4] || (_cache[4] = _createElementVNode("svg", {
                class: "animate-spin h-8 w-8 text-blue-500",
                fill: "none",
                stroke: "currentColor",
                viewBox: "0 0 24 24"
              }, [
                _createElementVNode("path", {
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round",
                  "stroke-width": "2",
                  d: "M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                })
              ], -1)),
              _createElementVNode("span", {
                class: _normalizeClass([
              _unref(theme).isDark.value ? 'text-gray-300' : 'text-gray-600',
              'ml-3 text-lg'
            ])
              }, " Loading audit logs... ", 2)
            ])
          ], 2))
        : (_openBlock(), _createElementBlock("div", _hoisted_11, [
            _createVNode(AuditLogsTable, {
              "audit-logs": auditLogs.value,
              loading: loading.value
            }, null, 8, ["audit-logs", "loading"])
          ])),
      (pagination.hasMore && !loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_12, [
            _createElementVNode("button", {
              onClick: loadMore,
              class: _normalizeClass([
            _unref(theme).isDark.value
              ? 'bg-gray-700 hover:bg-gray-600 text-gray-200'
              : 'bg-white hover:bg-gray-50 text-gray-700',
            'px-6 py-3 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium transition-colors'
          ])
            }, " Load More ", 2)
          ]))
        : _createCommentVNode("", true)
    ])
  ]))
}
}

})